import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { addLocale, locale as setPrimeLocale } from 'primereact/api';
import sr from '../locale/sr';
import { useAuth } from './AuthContext';
import { AppLanguage, isAppLanguage } from '../i18n/types';
import { translateCustomerPortal, translateNavLabel } from '../i18n/customerPortal';

interface LanguageContextType {
  lang: AppLanguage;
  isCustomerPortal: boolean;
  t: (key: string, params?: Record<string, string>) => string;
  tNav: (label: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};

/** Safe hook — returns null t() for non-customer contexts if needed outside provider tree edge cases. */
export const useOptionalLanguage = () => useContext(LanguageContext);

function resolveCustomerLang(emailLanguage?: string): AppLanguage {
  return isAppLanguage(emailLanguage) ? emailLanguage : 'en';
}

function applyPrimeLocale(lang: AppLanguage) {
  if (lang === 'sr') {
    addLocale('sr', sr);
    setPrimeLocale('sr');
  } else {
    setPrimeLocale('en');
  }
}

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const isCustomerPortal = user?.role === 'customer';
  const lang = isCustomerPortal ? resolveCustomerLang(user?.emailLanguage) : 'sr';

  useEffect(() => {
    applyPrimeLocale(isCustomerPortal ? lang : 'sr');
  }, [lang, isCustomerPortal]);

  const value = useMemo<LanguageContextType>(() => ({
    lang,
    isCustomerPortal,
    t: (key, params) => (isCustomerPortal ? translateCustomerPortal(lang, key, params) : key),
    tNav: (label) => (isCustomerPortal ? translateNavLabel(lang, label) : label)
  }), [lang, isCustomerPortal]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
